import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "analyticsClient", "originProduct", "redirectToWac", "env", "plugins", "globalExperimentalOptions", "onError"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { Suspense, useCallback, useMemo, useState, useRef } from 'react';
import { BaseCrossFlowApiProvider, Journeys } from '@atlassiansox/cross-flow-api-internals';
import { getGlobalTheme } from '@atlaskit/tokens';
import { analyticsWrapper } from './analytics-helpers/analyticsHelpers';
import { transformEvent } from './analytics-helpers/analyticsTransformers';
import { setProductSignUpLocation } from './redirects-helpers/redirectsHelpers';
import { ErrorBoundary } from './errorBoundary';
import { enrichWithPackageDetails } from './enrichWithPackageDetails';
import { enrichWithRequestOptions } from './enrichWithRequestOptions';
import { OPERATIONAL_EVENT_TYPE, source, uiInitialized, crossFlowActionSubject, openInvoked } from './constants';
var IntegrationView = /*#__PURE__*/React.lazy(function () {
  return import( /* webpackChunkName: "atlassiansox-cross-flow-support-deferred" */
  /* webpackPrefetch: true */
  './view');
});
/**
 * Generic Cross Flow API provider.
 *
 * Initializes Cross Flow support in product.
 * Renders Cross Flow overlay when requested via context API.
 */

export var createCrossFlowProvider = function createCrossFlowProvider(IntegrationView) {
  return function (props) {
    var _requestOptions$zInde;
    var children = props.children,
      analyticsClient = props.analyticsClient,
      originProduct = props.originProduct,
      redirectToWac = props.redirectToWac,
      env = props.env,
      _props$plugins = props.plugins,
      plugins = _props$plugins === void 0 ? [] : _props$plugins,
      globalExperimentalOptions = props.globalExperimentalOptions,
      onError = props.onError,
      baseProviderProps = _objectWithoutProperties(props, _excluded);
    var onCloseResolver = useRef();
    var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      firstHandshakeReceived = _useState2[0],
      setFirstHandshakeReceived = _useState2[1];
    var fireEvent = useMemo(function () {
      return analyticsWrapper(analyticsClient);
    }, [analyticsClient]);
    var _useState3 = useState(),
      _useState4 = _slicedToArray(_useState3, 2),
      requestOptions = _useState4[0],
      setRequestOptions = _useState4[1];
    var onAnalyticsEvent = useCallback(function (payload) {
      var gasV3Event = transformEvent(payload);
      if (gasV3Event) {
        var enrichedEvent = enrichWithRequestOptions(enrichWithPackageDetails(gasV3Event), requestOptions);
        fireEvent(enrichedEvent);
      }
    }, [fireEvent, requestOptions]);
    var onHandShake = useCallback(function (appName) {
      if (!firstHandshakeReceived) {
        onAnalyticsEvent({
          payload: {
            eventType: OPERATIONAL_EVENT_TYPE,
            action: uiInitialized,
            actionSubject: crossFlowActionSubject,
            attributes: {
              appName: appName
            }
          },
          context: [{
            source: source
          }]
        });
        setFirstHandshakeReceived(true);
      }
    }, [firstHandshakeReceived, onAnalyticsEvent]);
    var onOpen = useCallback(function (options) {
      /**
       * Reset first handshake state to ensure 1:1 value of open invoked and handshake received
       * Caveat that if open api is invoked while cross flow support UI is already mounted there will be mismatch of openInvoked events to uiInitialized events
       */
      setFirstHandshakeReceived(false);
      var analyticsEvent = {
        // setRequestOptions does not update the state right away (updates on the next render) so for the openInvoked event we need to manually append the sourceComponent and sourceContext
        payload: enrichWithRequestOptions({
          eventType: OPERATIONAL_EVENT_TYPE,
          action: openInvoked,
          actionSubject: crossFlowActionSubject
        }, options),
        context: [{
          source: source
        }]
      };
      onAnalyticsEvent(analyticsEvent);

      // Short circuit requestOptions for WAC expansions
      if (options.journey === Journeys.GET_STARTED && redirectToWac) {
        setProductSignUpLocation(options.targetProduct, env, options.sourceComponent, options.sourceContext);
        var _completionStatus = {};
        return Promise.resolve(_completionStatus);
      }
      var updatedExperimentalOptions = _objectSpread(_objectSpread({}, globalExperimentalOptions || {}), options.experimentalOptions || {});
      options.experimentalOptions = updatedExperimentalOptions;
      setRequestOptions(options);
      return new Promise(function (resolve) {
        onCloseResolver.current = resolve;
      });
    }, [onAnalyticsEvent, env, redirectToWac, globalExperimentalOptions]);
    var onClose = useCallback(function (completionStatus) {
      setFirstHandshakeReceived(false);
      setRequestOptions(undefined);
      onCloseResolver.current && onCloseResolver.current(completionStatus);
    }, []);
    var _getGlobalTheme = getGlobalTheme(),
      colorMode = _getGlobalTheme.colorMode;
    return /*#__PURE__*/React.createElement(BaseCrossFlowApiProvider, {
      onOpen: onOpen
    }, children, requestOptions && /*#__PURE__*/React.createElement(ErrorBoundary, {
      onAnalyticsEvent: onAnalyticsEvent,
      onError: onError
    }, /*#__PURE__*/React.createElement(Suspense, {
      fallback: null
    }, /*#__PURE__*/React.createElement(IntegrationView, _extends({}, baseProviderProps, requestOptions, {
      onAnalyticsEvent: onAnalyticsEvent,
      originProduct: originProduct,
      theme: colorMode,
      onClose: onClose,
      onHandShake: onHandShake,
      plugins: plugins,
      env: env,
      redirectToWac: redirectToWac,
      zIndex: (_requestOptions$zInde = requestOptions.zIndex) !== null && _requestOptions$zInde !== void 0 ? _requestOptions$zInde : 1000
    })))));
  };
};
export var CrossFlowProvider = createCrossFlowProvider(IntegrationView);